import {useState} from 'react';
import './TextMessageCourtFormatter.css';
import {writePendingTextMessageHandoff} from './fenzsafeTextMessageHandoff';

/**
 * Free, unauthenticated, entirely client-side "print text messages for court" tool (Issue #114,
 * FenzSafe research handoff 2026-09-22 -- Antigravity's SEO/PLG research names this the single
 * most-validated, lowest-competition, fastest-growing search term in the whole corpus, and the
 * top-of-funnel PLG wedge into FenzSafe). Nothing pasted here is uploaded anywhere unless the
 * visitor explicitly clicks "Add this to my FenzSafe matter" below -- parsing, hashing, and the
 * printable layout otherwise all happen in this component, in the visitor's own browser.
 *
 * Deliberately honest about what the hash proves. Per the same research's own non-legal-advice
 * guardrails ("hashing is byte integrity only and never implies authorship, admissibility, or
 * chain-of-custody prior to upload"), this tool never claims to make anything admissible -- it
 * only proves the printed page matches exactly what was pasted into the box. A retyped or
 * screenshotted conversation is weaker evidence than a real device export; the copy below says so
 * rather than overselling this tool's evidentiary value.
 *
 * "Add this to my FenzSafe matter" (Issue #114 founder direction, comment 5774592884, CE priority
 * #6) hands the formatted record off to the real, authenticated FenzSafe workspace via
 * `fenzsafeTextMessageHandoff.ts` -- see that module's doc comment for why a same-tab
 * `sessionStorage` note, consumed by `FenzSafeApp.tsx`, is the smallest safe mechanism rather than
 * this unauthenticated page calling the document-upload contract directly. Clicking it performs no
 * network request itself; it only stores the note locally and navigates to the FenzSafe workspace,
 * where a second explicit action is what actually creates the document.
 */

type ParsedMessage = {
  sender: string;
  text: string;
};

const SENDER_LINE = /^([^:\n]{1,40}):\s*(.+)$/;

/** Exported for focused unit testing -- pure parsing, no component state involved. */
export function parseConversation(raw: string): ParsedMessage[] {
  const lines = raw.split(/\r\n|\r|\n/);
  const messages: ParsedMessage[] = [];
  for (const line of lines) {
    if (line.trim() === '') continue;
    const match = SENDER_LINE.exec(line);
    if (match) {
      messages.push({sender: match[1].trim(), text: match[2].trim()});
    } else if (messages.length > 0) {
      // A continuation line of the previous message (multi-line text), not a new one.
      messages[messages.length - 1] = {...messages[messages.length - 1], text: `${messages[messages.length - 1].text}\n${line.trim()}`};
    } else {
      // Nothing parsed as "Sender: message" yet -- still show the content rather than
      // silently dropping it, honestly labeled as unattributed.
      messages.push({sender: 'Unlabeled', text: line.trim()});
    }
  }
  return messages;
}

/** Same hex-encoding idiom already used for evidence hashing elsewhere in this app (api.ts's submitAttemptEvidence()). */
async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

type FormattedResult = {messages: ParsedMessage[]; hash: string; generatedAt: string; source: string};

/**
 * Exact plain-text rendering of the "Text Message Record" shown/printed above -- reused verbatim
 * as the uploaded document's own content if the visitor later clicks "Add this to my FenzSafe
 * matter", so the record a customer's attorney eventually opens in FenzSafe is byte-for-byte what
 * this page actually showed them, not a second, independently-built copy that could drift from it.
 */
export function buildRecordText(result: FormattedResult, caseName: string, otherParty: string): string {
  const lines: string[] = ['FenzSafe -- Text Message Record', ''];
  if (caseName.trim()) lines.push(`Case: ${caseName.trim()}`);
  if (otherParty.trim()) lines.push(`Other party: ${otherParty.trim()}`);
  lines.push(`Formatted on ${result.generatedAt}`);
  lines.push(`SHA-256 of pasted text: ${result.hash}`);
  lines.push('');
  for (const message of result.messages) {
    lines.push(`${message.sender}: ${message.text}`);
  }
  return lines.join('\n');
}

/** A safe, human-legible file name -- generatedAt is a locale date/time string that can contain slashes/colons. */
function recordFileName(generatedAt: string): string {
  const slug = generatedAt.replace(/[^0-9A-Za-z]+/g, '-').replace(/^-+|-+$/g, '');
  return `text-message-record-${slug || 'record'}.txt`;
}

export function TextMessageCourtFormatter() {
  const [rawInput, setRawInput] = useState('');
  const [caseName, setCaseName] = useState('');
  const [otherParty, setOtherParty] = useState('');
  const [result, setResult] = useState<FormattedResult | null>(null);
  const [hashing, setHashing] = useState(false);
  const [addedToFenzSafe, setAddedToFenzSafe] = useState(false);

  async function formatAndHash() {
    if (rawInput.trim() === '' || hashing) return;
    setHashing(true);
    try {
      const hash = await sha256Hex(rawInput);
      setResult({messages: parseConversation(rawInput), hash, generatedAt: new Date().toLocaleString(), source: rawInput});
      setAddedToFenzSafe(false);
    } finally {
      setHashing(false);
    }
  }

  function addToFenzSafeMatter() {
    if (!result) return;
    writePendingTextMessageHandoff({
      fileName: recordFileName(result.generatedAt),
      documentText: buildRecordText(result, caseName, otherParty),
      caseName,
      otherParty,
      generatedAt: result.generatedAt,
      hash: result.hash,
    });
    setAddedToFenzSafe(true);
    window.location.assign('/fenzsafe');
  }

  return (
    <main className="tmcf-root">
      <section className="tmcf-intro no-print">
        <p className="tmcf-brand" aria-hidden="true">
          FenzSafe
        </p>
        <h1>Print text messages for court</h1>
        <p>
          Paste a text-message conversation below and get a clean, dated, hash-verified printout. Nothing you paste is uploaded
          anywhere unless you choose to add it to your FenzSafe matter -- everything else happens entirely in your own browser.
        </p>
        <p className="tmcf-format-hint">
          For best results, paste one message per line as <code>Sender: message text</code> (most phones' copy/export already
          look like this). Anything that doesn't match is still included, just unlabeled.
        </p>
      </section>

      <section className="tmcf-form no-print" aria-labelledby="tmcf-form-heading">
        <h2 id="tmcf-form-heading">Your conversation</h2>
        <div className="tmcf-field">
          <label htmlFor="tmcf-case-name">Case name or reference (optional)</label>
          <input id="tmcf-case-name" type="text" value={caseName} onChange={(event) => setCaseName(event.target.value)} placeholder="e.g. Smith v. Jones" />
        </div>
        <div className="tmcf-field">
          <label htmlFor="tmcf-other-party">Who you were messaging (optional)</label>
          <input id="tmcf-other-party" type="text" value={otherParty} onChange={(event) => setOtherParty(event.target.value)} placeholder="e.g. John Smith" />
        </div>
        <div className="tmcf-field">
          <label htmlFor="tmcf-raw-input">Pasted conversation</label>
          <textarea
            id="tmcf-raw-input"
            value={rawInput}
            onChange={(event) => setRawInput(event.target.value)}
            rows={12}
            placeholder={'Me: Can you pick up the kids Friday?\nJohn: No, that’s your week.'}
          />
        </div>
        <button type="button" className="tmcf-generate-button" onClick={formatAndHash} disabled={rawInput.trim() === '' || hashing}>
          {hashing ? 'Formatting…' : 'Format & hash for printing'}
        </button>
      </section>

      {result && (
        <section className="tmcf-preview" aria-labelledby="tmcf-preview-heading">
          <div className="tmcf-preview-header">
            <p className="tmcf-brand" aria-hidden="true">
              FenzSafe
            </p>
            <h2 id="tmcf-preview-heading">Text Message Record</h2>
            {caseName && <p className="tmcf-preview-meta">Case: {caseName}</p>}
            {otherParty && <p className="tmcf-preview-meta">Other party: {otherParty}</p>}
            <p className="tmcf-preview-meta">Formatted on {result.generatedAt}</p>
            <p className="tmcf-preview-hash">SHA-256 of pasted text: {result.hash}</p>
          </div>
          <ol className="tmcf-messages">
            {result.messages.map((message, index) => (
              <li key={index} className="tmcf-message">
                <span className="tmcf-sender">{message.sender}:</span> <span className="tmcf-text">{message.text}</span>
              </li>
            ))}
          </ol>
          <div className="tmcf-preview-actions no-print">
            <button type="button" className="tmcf-print-button" onClick={() => window.print()}>
              Print / save as PDF
            </button>
            <button type="button" className="tmcf-fenzsafe-button" onClick={addToFenzSafeMatter} disabled={addedToFenzSafe}>
              {addedToFenzSafe ? 'Opening your FenzSafe workspace…' : 'Add this to my FenzSafe matter'}
            </button>
          </div>
          <p className="tmcf-fenzsafe-note no-print">
            Adding this takes you to your FenzSafe workspace, where you choose the matter and confirm before anything is saved --
            you&rsquo;ll need to sign in to FenzSafe first if you aren&rsquo;t already.
          </p>
        </section>
      )}

      <section className="tmcf-disclaimer no-print" aria-label="What this tool does and does not do">
        <p>
          This tool formats your pasted text and computes a SHA-256 hash of exactly what you entered, so a printed copy can be
          shown to match what you pasted here. It does <strong>not</strong> determine whether this is admissible evidence in your
          case, and this is not legal advice -- only a judge decides admissibility. Retyped or screenshotted text is weaker
          evidence than an original export from your phone; if your device or messaging app can export the real conversation as a
          file, ask your attorney or the court clerk whether that original export is required instead of a printout.
        </p>
        <p>
          FenzSafe is a free tool for organizing and securely sharing your case documents with an attorney.{' '}
          <a href="/">Learn more about FenzSafe {'→'}</a>
        </p>
      </section>
    </main>
  );
}
